import { useState } from "react";
import { Check } from "lucide-react";

import { toast } from "utils/toast";
import { detectTextLang, translations } from "utils/i18n";
import { formatDuration, languageLabel, transcriptName } from "utils/transcript";
import { cn } from "lib/utils";
import { useLanguage } from "context/languageContext";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "components/ui/dialog";
import { META_SM, SourceIcon } from "components/transcription-list";

// Every column but Name is a fixed width, and Name is the only 1fr track —
// it's the sole place that can shrink, so the row can never force the
// dialog wider than it is (no horizontal scroll, whatever the file names).
// Below `sm`, Language/Duration drop out of the grid entirely (see META_SM)
// rather than merely shrinking the Name column to a sliver — same
// progressive-disclosure the main transcription list uses, with the folded
// columns reappearing as a subtitle line under the name instead.
const IMPORT_GRID = [
  "grid items-center gap-x-3",
  "grid-cols-[2rem_minmax(0,1fr)_1.25rem]",
  "sm:grid-cols-[2rem_minmax(0,1fr)_4.5rem_4.5rem_1.25rem] sm:gap-x-4",
].join(" ");

function ImportRow({ item, selected, onToggle }) {
  const { language } = useLanguage();
  const name = transcriptName(item, language);

  return (
    <li>
      <button
        type="button"
        data-slot="button"
        onClick={() => onToggle(item.id)}
        aria-pressed={selected}
        className={cn(
          IMPORT_GRID,
          "w-full rounded-lg border-0 px-2.5 py-2.5 text-start outline-none transition-colors",
          "focus-visible:ring-1 focus-visible:ring-ring",
          selected ? "bg-accent" : "bg-transparent hover:bg-muted"
        )}
      >
        <SourceIcon source={item.source} />

        <div className="min-w-0">
          <span lang={detectTextLang(name)} className="block truncate text-[0.95rem] font-medium text-foreground">
            {name}
          </span>
          <p className="truncate text-xs text-muted-foreground sm:hidden">
            {languageLabel(item)} · {formatDuration(item.duration_seconds)}
          </p>
        </div>

        <span className={cn(META_SM, "truncate font-mono text-xs text-muted-foreground")}>{languageLabel(item)}</span>

        <span className={cn(META_SM, "truncate font-mono text-xs tabular-nums text-muted-foreground")}>
          {formatDuration(item.duration_seconds)}
        </span>

        <span
          className={cn(
            "flex size-5 items-center justify-center rounded-full border transition-colors",
            selected ? "border-blue-600 bg-blue-600" : "border-border bg-transparent"
          )}
        >
          {selected ? <Check className="size-3.5 text-white" aria-hidden="true" /> : null}
        </span>
      </button>
    </li>
  );
}

/**
 * Pulls one or more transcriptions that aren't in any project into this one.
 * Selecting is non-destructive (nothing moves until Import is confirmed), and
 * a failed item stays selected afterward so a retry doesn't require picking
 * everything again.
 */
export function ImportTranscriptionDialog({ open, onOpenChange, items, onImport }) {
  const { language } = useLanguage();
  const tr = translations[language].importDialog;
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);

  // Fresh selection every time the dialog opens.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setSelected(new Set());
  }

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleConfirm() {
    const toImport = items.filter((item) => selected.has(item.id));
    if (toImport.length === 0) return;

    setImporting(true);
    const results = await Promise.all(toImport.map((item) => onImport(item, { silent: true })));
    setImporting(false);

    const succeeded = results.filter(Boolean).length;
    if (succeeded > 0) {
      toast.success(tr.importedToast(succeeded));
    }

    if (succeeded === toImport.length) {
      onOpenChange(false);
    } else {
      // Leave only the failed picks selected so retrying doesn't start over.
      setSelected(new Set(toImport.filter((_, index) => !results[index]).map((item) => item.id)));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{tr.title}</DialogTitle>
          <DialogDescription>{tr.description}</DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            {tr.empty}
          </p>
        ) : (
          <div>
            <div className={cn(IMPORT_GRID, "px-2.5 pb-1.5 text-xs font-medium text-muted-foreground")}>
              <span aria-hidden="true" />
              <span>{tr.columnName}</span>
              <span className={META_SM}>{tr.columnLanguage}</span>
              <span className={META_SM}>{tr.columnDuration}</span>
              <span aria-hidden="true" />
            </div>

            <ul className="grid max-h-80 gap-0.5 overflow-y-auto border-t border-border pt-1">
              {items.map((item) => (
                <ImportRow key={item.id} item={item} selected={selected.has(item.id)} onToggle={toggle} />
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {tr.cancel}
          </Button>
          <Button variant="blue" disabled={selected.size === 0 || importing} onClick={handleConfirm}>
            {importing ? tr.importingLabel : tr.importButton(selected.size)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
